import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import style from "./UserProfilePopup.module.css";
import Button from "../button/Button";
import { RxCross2 } from "react-icons/rx";
import { MdContentCopy, MdCheck } from "react-icons/md";
import { getUserProfile, creatorGetProfile, stopImpersonation } from "../../api/HttpApis";
import { isImpersonating, endImpersonationLocally } from "../../utils/impersonation";
import { showToast } from "../../utils/Toastify/Toastify";
import useLocalStoreClear from "../../hooks/useLocalStoreClear";
import { FaUser, FaRegEdit, FaIdCard } from "react-icons/fa";
import CreatorEditProfile from "../creatorEditProfile/CreatorEditProfile";

const UserProfilePopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const { clearLocalStorage } = useLocalStoreClear();
  const [emailCopied, setEmailCopied] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);

  // Only creators may edit their profile (admins excluded). userData.role is
  // the string "Creator"/"Admin"/"Subscriber" returned by /profile. Image and
  // all profile editing now live inside the CreatorEditProfile modal — there is
  // no longer a separate image-change dialog here.
  const isCreator = userData?.role === "Creator";

  const handleCopyEmail = async (e) => {
    e.stopPropagation();
    const email = userData?.email;
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1500);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — fail silently.
    }
  };

  // Creators and staff/admins are served from different profile routes on the
  // backend (creators have their own RBAC-restricted /profile access), so pick
  // the endpoint from the role we already know from login/signup rather than
  // always hitting the staff route and getting an empty/denied response back.
  const userProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const isCreatorRole = localStorage.getItem("role") === "Creator";
      const response = isCreatorRole
        ? await creatorGetProfile()
        : await getUserProfile();
      const user = response?.data?.data;
      setUserData({
        ...user,
        role: isCreatorRole ? "Creator" : user?.role,
        profileImage: user?.profile_img,
      });
    } catch (err) {
      const message = err.response?.data?.message || "Couldn't load your profile";
      setError(message);
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    userProfile();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
    setIsVisible(true);
  }, [isOpen]);

  // Clicking the avatar is a shortcut to Edit Profile (creators only).
  const handleAvatarClick = () => {
    if (isCreator) setShowEditProfile(true);
  };

  const handleRetry = useCallback(() => {
    setRetryLoading(true);
    setTimeout(() => {
      setRetryLoading(false);
      userProfile();
    }, 1000);
  }, []);

  const handleLogout = useCallback(() => {
    // In a creator-portal session "Logout" means going back to the admin's
    // own session, not logging the admin out.
    if (isImpersonating()) {
      setLogoutLoading(true);
      stopImpersonation()
        .catch(() => {})
        .finally(endImpersonationLocally);
      return;
    }
    setLogoutLoading(true);
    setTimeout(() => {
      setLogoutLoading(false);
      clearLocalStorage();
      onClose();
      window.dispatchEvent(new Event("storage"));
      navigate("/");
    }, 1000);
  }, [navigate, onClose]);

  if (!isVisible) return null;

  return (
    <>
      <div
        className={`${style.popupContainer} ${
          isOpen ? style.open : style.closed
        }`}
      >
        <div className={style.popupContent}>
          {isLoading ? (
            <div className={style.loaderContainer}>
              <div className={style.loader} />
              <p className={style.loaderText}>Loading...</p>
            </div>
          ) : error ? (
            <div className={style.errorContainer}>
              <p className={style.errorText}>{error}</p>
              <Button loading={retryLoading} onClick={handleRetry}>
                Retry
              </Button>
              <Button loading={logoutLoading} onClick={handleLogout}>
                Logout
              </Button>
            </div>
          ) : (
            <div className={style.userInfo}>
              <div className={style.crossIcon}>
                <RxCross2
                  className={style.crossIcon1}
                  onClick={() => onClose()}
                />
              </div>

              <div className={style.infoBox}>
                <div
                  className={`${style.profileImageContainer} ${
                    isCreator ? style.profileImageEditable : ""
                  }`}
                  onClick={handleAvatarClick}
                  title={isCreator ? "Edit profile" : undefined}
                >
                  {userData?.profileImage ? (
                    <img
                      src={userData?.profileImage}
                      alt="Profile"
                      className={style.profileImage}
                    />
                  ) : (
                    <FaUser className={style.profileImage} />
                  )}
                </div>

                <div className={style.userDetails}>
                  <h3 className={style.userName} title={userData?.name}>
                    {userData?.name}
                  </h3>
                  <div className={style.emailRow}>
                    <span className={style.userEmail} title={userData?.email}>
                      {userData?.email}
                    </span>
                    {userData?.email && (
                      <button
                        type="button"
                        className={style.copyBtn}
                        onClick={handleCopyEmail}
                        aria-label="Copy email"
                        title={emailCopied ? "Copied!" : "Copy email"}
                      >
                        {emailCopied ? <MdCheck /> : <MdContentCopy />}
                      </button>
                    )}
                  </div>
                  <p className={style.userRole}>{userData?.role}</p>
                </div>
              </div>

              {/* Actions below the user info. Profile (view) and Edit Profile
                  are creators-only; everyone gets Logout. */}
              <div className={style.btnBox}>
                {isCreator && (
                  <Button
                    variant="secondary"
                    className={style.viewProfileButton}
                    iconLeft={<FaIdCard className={style.editProfileIcon} />}
                    onClick={() => {
                      onClose();
                      navigate("/mainlayout/creator-profile");
                    }}
                  >
                    Profile
                  </Button>
                )}
                {isCreator && (
                  <Button
                    variant="dark"
                    className={style.editProfileButton}
                    iconLeft={<FaRegEdit className={style.editProfileIcon} />}
                    onClick={() => setShowEditProfile(true)}
                  >
                    Edit Profile
                  </Button>
                )}
                <Button
                  variant="outline"
                  className={style.logoutButton}
                  loading={logoutLoading}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isCreator && showEditProfile && (
        <CreatorEditProfile
          isOpen={showEditProfile}
          onClose={() => setShowEditProfile(false)}
          onUpdated={userProfile}
        />
      )}
    </>
  );
};

export default UserProfilePopup;
